import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { ModalTitleValues, ModalVariant, ToastVariant } from '@/store/ui/types';
import { sendRecordingErrorMessage } from '@/iframe-messaging';
import { Answer } from 'altru-types';
import { AppFreeAnswerRecordingError } from 'altru-types/@types/app-free-events';

@Module({ namespaced: true })
export default class UiModule extends VuexModule {
  loading = false;
  loadingTerm = '';
  errorMessage = '';
  toastVariantValue: ToastVariant = 'error';

  /* Loading status */
  get isLoading(): boolean {
    return this.loading;
  }

  get loadingTermValue(): string {
    return this.loadingTerm;
  }

  @Mutation
  setIsLoading(value: boolean): void {
    this.loading = value;
  }

  @Mutation
  setLoadingTermValue(value: string): void {
    this.loadingTerm = value;
  }

  @Action
  toggleLoading({ value, loadingTerm }: { value?: boolean; loadingTerm?: string }): void {
    if (loadingTerm) {
      this.context.commit('setLoadingTermValue', loadingTerm);
    }
    if (value) {
      this.context.commit('setIsLoading', value);
    } else {
      this.context.commit('setIsLoading', !this.loading);
    }
  }

  /* Modals */
  modalTitleValue: ModalTitleValues = { title: '', subtitle: '' };
  modalStatus = false;
  modalType: ModalVariant = 'TERMS';

  get modalTitle(): ModalTitleValues {
    return this.modalTitleValue;
  }

  @Mutation
  setModalTitle(value: ModalTitleValues): void {
    this.modalTitleValue = value;
  }

  @Action
  toggleModalTitle(value: ModalTitleValues): void {
    this.context.commit('setModalTitle', value);
  }

  get showModal(): boolean {
    return this.modalStatus;
  }

  @Mutation
  setModalStatus(value: boolean): void {
    this.modalStatus = value;
  }

  @Action
  toggleModalStatus(value: boolean): void {
    this.context.commit('setModalStatus', value);
  }

  get modalVariant(): ModalVariant {
    return this.modalType;
  }

  @Mutation
  setModalType(value: ModalVariant): void {
    this.modalType = value;
  }

  @Action
  toggleModalType(value: ModalVariant): void {
    this.context.commit('setModalType', value);
  }

  /* Error message */
  get errorMessageValue(): string {
    return this.errorMessage;
  }

  get toastVariant(): ToastVariant {
    return this.toastVariantValue;
  }

  @Mutation
  setErrorMessageValue(value: string): void {
    this.errorMessage = value;
  }

  @Mutation
  setToastVariant(value: ToastVariant): void {
    this.toastVariantValue = value;
  }

  @Action
  setErrorMessage({
    value,
    variant = 'info',
    persistent,
  }: {
    value: string;
    variant: ToastVariant;
    persistent?: boolean;
  }): void {
    // Emit error message to parent
    const isIframed = this.context.rootGetters['status/isIframed'];
    if (isIframed) {
      sendRecordingErrorMessage({
        kind: 'answer recording error',
        level: variant as AppFreeAnswerRecordingError['level'],
        message: value,
        answerId: (this.context.rootGetters['answers/payloadAnswerInfo'] as Answer)?.id,
      });
    } else {
      sendRecordingErrorMessage({
        kind: 'answer recording error',
        level: variant as AppFreeAnswerRecordingError['level'],
        message: value,
        answerId: 'unknown',
      });
    }

    if (variant) {
      this.context.commit('setToastVariant', variant);
    }
    this.context.commit('setErrorMessageValue', value);
    if (!persistent) {
      // toast clean up
      setTimeout(() => this.context.commit('setErrorMessageValue', ''), 7500);
    }
  }

  @Action
  clearErrorMessage(): void {
    this.context.commit('setErrorMessageValue', '');
    this.context.commit('setToastVariant', 'error');
  }
}
